import * as React from 'react';
import { z } from 'zod';
import { useTranslations } from 'next-intl';

import { Input, type InputProps } from './Input';

export interface EmailInputProps extends Omit<InputProps, 'type' | 'onChange'> {
  onChange?: (value: string, isValid: boolean) => void;
  invalidMessage?: string;
  children?: React.ReactNode;
}

const emailSchema = z.email();

export const EmailInput = React.forwardRef<HTMLInputElement, EmailInputProps>(
  ({ onChange, error, invalidMessage, children, ...rest }, ref) => {
    const t = useTranslations();
    const [validationError, setValidationError] = React.useState<string | undefined>();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = e.target.value;
      const isEmpty = nextValue.trim() === '';
      const isValid = isEmpty || emailSchema.safeParse(nextValue).success;

      setValidationError(
        isValid
          ? undefined
          : (invalidMessage ?? t('checkout_page.validation.email.invalid')),
      );
      onChange?.(nextValue, isValid);
    };

    return (
      <div>
        <Input
          ref={ref}
          type="email"
          autoComplete="email"
          inputMode="email"
          onChange={handleChange}
          error={error ?? validationError}
          {...rest}
        />
        {children}
      </div>
    );
  },
);

EmailInput.displayName = 'EmailInput';
